/**
 * @ipc/admin-ui · entities/session · api — вызовы /admin/auth/*.
 * login (Telegram Login Widget) → {accessToken,refreshToken};
 * refresh → {accessToken,refreshToken} (rotation, refresh TTL 30 дней sliding);
 * logout → {ok}; me → SessionUser. Токены кладём в localStorage (token-store); Bearer
 * шлёт apiClient автоматически.
 */
import { request, ApiError } from '@/shared/api';
import { isPlainObject, str, strList, optNum } from '@/shared/api';
import { setTokens, clearTokens } from '@/shared/api';
import type { SessionUser, AuthTokens } from '../model/types';
import type { TelegramLoginDto } from '../model/schema';

function toAuthTokens(raw: unknown): AuthTokens {
  const obj = isPlainObject(raw) ? raw : {};
  return {
    accessToken: str(obj['accessToken']),
    refreshToken: str(obj['refreshToken']),
  };
}

/** id из /me приходит ЧИСЛОМ (AdminUser.id, admin-auth.controller /me) — нормализуем к строке. */
function idToString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

function toSessionUser(raw: unknown): SessionUser | null {
  if (!isPlainObject(raw)) {
    return null;
  }
  const id = idToString(raw['id']);
  const tgUsername = str(raw['tgUsername']);
  if (id.length === 0 || tgUsername.length === 0) {
    return null;
  }
  const role = typeof raw['role'] === 'string' ? raw['role'] : null;
  return {
    id,
    tgUsername,
    telegramId: optNum(raw['telegramId']),
    role,
    permissions: strList(raw['permissions']),
    merchantIds: strList(raw['merchantIds']),
  };
}

/** POST /admin/auth/telegram — обменивает подписанную полезную нагрузку Telegram Login Widget на JWT-пару, сохраняет её локально. */
export async function login(body: TelegramLoginDto): Promise<AuthTokens> {
  const { data } = await request('/admin/auth/telegram', { method: 'POST', body });
  const tokens = toAuthTokens(data);
  setTokens(tokens.accessToken, tokens.refreshToken);
  return tokens;
}

/** Исход авто-входа через TWA: успех / отказ бэка (не админ, нет привязки) / сетевая ошибка. */
export type TwaLoginResult = { kind: 'ok' } | { kind: 'denied' } | { kind: 'error' };

/**
 * POST /admin/auth/telegram-twa — авто-вход по сырому initData из Telegram Mini App.
 * При успехе кладёт JWT-пару в localStorage (как обычный login). 401 от бэка =
 * осмысленный отказ (пользователь не заведён / telegramId не привязан) → 'denied',
 * чтобы UI показал fallback (Login Widget), а не крутил ошибку.
 */
export async function twaLogin(initData: string): Promise<TwaLoginResult> {
  try {
    const { data } = await request('/admin/auth/telegram-twa', { method: 'POST', body: { initData } });
    const tokens = toAuthTokens(data);
    if (tokens.accessToken.length === 0) {
      return { kind: 'error' };
    }
    setTokens(tokens.accessToken, tokens.refreshToken);
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      return { kind: 'denied' };
    }
    return { kind: 'error' };
  }
}

/**
 * POST /admin/auth/refresh — ручной обмен refresh → JWT-пара с rotation (для явных вызовов UI).
 * Backend возвращает { accessToken, refreshToken }; refresh TTL 30 дней sliding.
 * Авто-refresh при 401 / proactive TTL делает api-client (single-flight); оба пути
 * пишут setTokens(access, refresh).
 */
export async function refresh(refreshToken: string): Promise<AuthTokens> {
  const { data } = await request('/admin/auth/refresh', { method: 'POST', body: { refreshToken } });
  const tokens = toAuthTokens(data);
  if (tokens.accessToken.length > 0) {
    if (tokens.refreshToken.length > 0) {
      setTokens(tokens.accessToken, tokens.refreshToken);
    } else {
      setTokens(tokens.accessToken);
    }
  }
  return tokens;
}

/** POST /admin/auth/logout — гасит сессию на бэке и локально. */
export async function logout(): Promise<void> {
  try {
    await request('/admin/auth/logout', { method: 'POST' });
  } finally {
    clearTokens();
  }
}

/** GET /admin/auth/me — текущий пользователь (id/tgUsername/telegramId/role/permissions/merchantIds). */
export async function fetchSession(): Promise<SessionUser | null> {
  const { data } = await request('/admin/auth/me');
  return toSessionUser(data);
}
